import React from "react";
import { cn } from "../../../../../lib/utils";
import { DishRowData } from "../costPriceDataTypes";
import { CategoryBadge } from "../../../../../utils/categoryHelper";
import { ColumnFilterDropdown } from "../../../../ui/ColumnFilterDropdown";
import { ArrowUp, ArrowDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isPracticeId } from "../../../../onboarding/practice/practiceGuards";
import { elevatedBg, secondaryText } from "../../../../../styling/stylingHelper";
import InfoHelper from "../../../../common/InfoHelper";
import { CostTargets, DEFAULT_COST_TARGETS, getCostTargetForDishType, isCostBad as isCostBadFor } from "../../../../../utils/costTargets";

interface DishTableProps {
    rows: DishRowData[];
    onDishClick?: (dish: DishRowData) => void;
    onCategoryClick?: (dish: DishRowData) => void;
    matrixFilters: string[];
    onMatrixFiltersChange: (filters: string[]) => void;
    allData: DishRowData[];
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc' | null;
    onSort: (column: string) => void;
    costTargets?: CostTargets;
}

const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(value);

const getMatrixBgColor = (matrix: string | null): string => {
    if (matrix === "Star") return "#16B364";
    if (matrix === "Dog") return "#F04438";
    if (matrix === "Puzzle") return "#2E90FA";
    if (matrix === "Plowhorse") return "#F1A62D";
    return "#E5E7EB";
};

const MatrixIcon: React.FC<{ matrix: string | null }> = ({ matrix }) => {
    if (!matrix) return null;

    let src = "";
    let alt = "";

    if (matrix === "Star") {
        src = "/assets/star.svg";
        alt = "Star";
    } else if (matrix === "Dog") {
        src = "/assets/dog.svg";
        alt = "Dog";
    } else if (matrix === "Puzzle") {
        src = "/assets/puzzle.svg";
        alt = "Puzzle";
    } else if (matrix === "Plowhorse") {
        src = "/assets/plowhorse.svg";
        alt = "Plowhorse";
    }

    if (!src) return null;

    return <img src={src} alt={alt} width={20} height={20} className="min-w-5" />;
};

// Sort icon component
const SortIcon: React.FC<{ 
    column: string; 
    sortColumn: string | null; 
    sortDirection: 'asc' | 'desc' | null;
    onSort: (column: string) => void;
}> = ({ column, sortColumn, sortDirection, onSort }) => {
    const isActive = sortColumn === column;
    const isAsc = isActive && sortDirection === 'asc';
    const isDesc = isActive && sortDirection === 'desc';

    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                onSort(column);
            }}
            className="ml-1.5 flex flex-row items-center justify-center gap-0 hover:opacity-70 transition-opacity"
            aria-label={`Sort by ${column}`}
        >
            <ArrowUp 
                className={cn(
                    "w-3 h-3",
                    isAsc ? "text-[#0E8965] dark:text-darkGreenColor-dark" : "text-[#9CA3AF] dark:text-textTertiary-dark"
                )} 
            />
            <ArrowDown 
                className={cn(
                    "w-3 h-3",
                    isDesc ? "text-[#0E8965] dark:text-darkGreenColor-dark" : "text-[#9CA3AF] dark:text-textTertiary-dark"
                )} 
            />
        </button>
    );
};

const DishTable: React.FC<DishTableProps> = ({ rows, onDishClick, onCategoryClick, matrixFilters, onMatrixFiltersChange, allData, sortColumn, sortDirection, onSort, costTargets = DEFAULT_COST_TARGETS }) => {
    const { t } = useTranslation();
    // Standard matrix options (always available for filtering)
    // Always show all matrix options so users can filter by any type
    const matrixOptions = [
        { id: 'Star', name: 'Star' },
        { id: 'Dog', name: 'Dog' },
        { id: 'Puzzle', name: 'Puzzle' },
        { id: 'Plowhorse', name: 'Plowhorse' }
    ];

    // Guided tour anchors. `cost-dish-first` must land on a row that already exists:
    // opening a placeholder row CREATES a cost price analysis (see handleItemClick in
    // CostPricePageNew), which a tour must never do on the user's behalf.
    const firstRealRowId = rows.find(row => !row.id.startsWith('placeholder-'))?.id;

    return (
        <div className="w-full overflow-x-auto">
            <table className="min-w-full">
                <thead>
                    <tr className="bg-white dark:bg-background-dark border-y border-[#EAECF0] dark:border-borderPrimary-dark text-nowrap">
                        <th className="px-4 py-[13px] text-left text-[12px] font-medium text-[#475467] dark:text-textTertiary-dark">
                            <div className="flex items-center">
                                {t('costPrice.table.dishName')}
                                <SortIcon column="dishName" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                            </div>
                        </th>
                        <th className="px-4 py-[13px] text-left text-[12px] font-medium text-[#475467] dark:text-textTertiary-dark">{t('costPrice.table.category')}</th>
                        <th className="px-4 py-[13px] text-left text-[12px] font-medium text-[#475467] dark:text-textTertiary-dark">
                            <div className="flex items-center gap-0.5">
                                {t('costPrice.table.matrix')}
                                <ColumnFilterDropdown
                                    selectedValues={matrixFilters}
                                    onValuesChange={onMatrixFiltersChange}
                                    availableOptions={matrixOptions}
                                    valueType="default"
                                    filterLabel={t('costPrice.table.matrix')}
                                    maxHeight="max-h-[200px]"
                                />
                            </div>
                        </th>
                        <th className="px-4 py-[13px] text-left text-[12px] font-medium text-[#475467] dark:text-textTertiary-dark">
                            <div className="flex items-center">
                                {t('costPrice.table.cost')}
                                <SortIcon column="cost" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                            </div>
                        </th>
                        <th className="px-4 py-[13px] text-left text-[12px] font-medium text-[#475467] dark:text-textTertiary-dark">
                            <div className="flex items-center">
                                {t('costPrice.table.price')}
                                <SortIcon column="price" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                            </div>
                        </th>
                        <th className="px-4 py-[13px] text-left text-[12px] font-medium text-[#475467] dark:text-textTertiary-dark">
                            <div className="flex items-center">
                                {t('costPrice.table.costPercentage')}
                                <SortIcon column="costPercentage" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                            </div>
                        </th>
                        <th className="px-4 py-[13px] text-left text-[12px] font-medium text-[#475467] dark:text-textTertiary-dark" data-onboarding="cost-col-margin">
                            <div className="flex items-center">
                                {t('costPrice.table.margin')}
                                <SortIcon column="margin" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                            </div>
                        </th>
                        <th className="px-4 py-[13px] text-left text-[12px] font-medium text-[#475467] dark:text-textTertiary-dark">
                            <div className="flex items-center gap-1">
                                <InfoHelper
                                    titleKey="onboardingHelper.popularity.title"
                                    descriptionKey="onboardingHelper.popularity.description"
                                />
                                {t('costPrice.table.popularity')}
                                <SortIcon column="popularity" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
                            </div>
                        </th>
                        <th className="px-4 py-[13px]" />
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td
                                colSpan={9}
                                className="px-4 pt-6 text-center text-[13px] text-[#6B7280] dark:text-textTertiary-dark"
                            >
                                {t('costPrice.table.noItemsFound')}
                            </td>
                        </tr>
                    ) : (
                        rows.map((row) => {
                            const rowTarget = getCostTargetForDishType(row.revenueSourceType, costTargets);
                            const isCostBad = isCostBadFor(row.costPercentage, rowTarget);
                            // Unfinished cost prices stay prominent unless explicitly marked irrelevant.
                            const showOpacity = row.isUncategorizedPlaceholder || row.markedIrrelevant;
                            const isPlaceholder = row.isPlaceholder || row.totalCost === 0;
                            // CHOICE rows (shared per-pick costs) carry no menu price / popularity /
                            // matrix — every column except name / category / per-pick cost shows "--".
                            const dash = <span className="text-[#6B7280] dark:text-textTertiary-dark">--</span>;

                            const onboardingIds = [
                                row.id === firstRealRowId ? 'cost-dish-first' : null,
                                isPracticeId(row.id) ? 'cost-dish-zero cost-dish-walkthrough' : null,
                            ].filter(Boolean).join(' ');

                            return (
                                <tr
                                    key={row.id}
                                    data-onboarding={onboardingIds || undefined}
                                    className={cn(
                                        "border-b border-[#E5E7EB] dark:border-borderPrimary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark cursor-pointer",
                                        showOpacity && "opacity-60"
                                    )}
                                    onClick={() => onDishClick && onDishClick(row)}
                                >
                                    <td className="px-4 py-5 text-[14px] leading-5 font-medium text-[#101828] dark:text-textPrimary-dark whitespace-nowrap max-w-[200px] overflow-hidden text-ellipsis">
                                        {row.markedIrrelevant ? (
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="truncate" title={row.displayName || row.dishName}>{row.displayName || row.dishName}</span>
                                                <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] leading-4 font-medium', elevatedBg, secondaryText)}>
                                                    {t('costPrice.notRelevant')}
                                                </span>
                                            </div>
                                        ) : (row.displayName || row.dishName)}
                                    </td>
                                    <td className="px-4 py-5 whitespace-nowrap">
                                        {onCategoryClick ? (
                                            <button
                                                className="hover:opacity-80 transition-all"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onCategoryClick(row);
                                                }}
                                            >
                                                <CategoryBadge
                                                    categoryName={row.category}
                                                    categoryColor={row.categoryColor}
                                                    isDefaultCategory={row.isDefaultCategory}
                                                />
                                            </button>
                                        ) : (
                                            <CategoryBadge
                                                categoryName={row.category}
                                                categoryColor={row.categoryColor}
                                                isDefaultCategory={row.isDefaultCategory}
                                            />
                                        )}
                                    </td>
                                    <td className="px-4 py-5 whitespace-nowrap">
                                        {row.isChoiceCost ? dash : row.matrix === null ? (
                                            <span className="text-[#6B7280] dark:text-textTertiary-dark">--</span>
                                        ) : (
                                            <span
                                                className="inline-flex items-center gap-[3px] rounded-full px-2.5 py-[5px] text-white dark:text-white"
                                                style={{ backgroundColor: getMatrixBgColor(row.matrix) }}
                                            >
                                                <MatrixIcon matrix={row.matrix} />
                                                <span className="text-[14px] leading-[10px] font-semibold -mt-[1px]">{row.matrix}</span>
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-5 text-left text-[14px] leading-5 font-normal text-[#475467] dark:text-textSecondary-dark whitespace-nowrap">
                                        {row.isChoiceCost && row.totalCost === 0 ? dash : isPlaceholder ? "-" : formatCurrency(row.totalCost)}
                                    </td>
                                    <td className="px-4 py-5 text-left text-[14px] leading-5 font-normal text-[#475467] dark:text-textSecondary-dark whitespace-nowrap">
                                        {row.isChoiceCost ? dash : row.price === 0 ? "-" : formatCurrency(row.price)}
                                    </td>
                                    <td className="px-4 py-5 text-left whitespace-nowrap">
                                        {row.isChoiceCost ? dash : isPlaceholder || row.price === 0 ? (
                                            <span className="text-[#6B7280] dark:text-textTertiary-dark">-</span>
                                        ) : (
                                            <span
                                                className={`inline-flex items-center rounded-md p-2 text-[14px] leading-[10px] font-semibold ${isCostBad
                                                    ? "bg-[#FFF4E6] dark:bg-[#FFF4E6]/20 text-[#eb8d00] dark:text-[#eb8d00]"
                                                    : "bg-[#E7FBF5] dark:bg-[#E7FBF5]/20 text-[#069A6E] dark:text-[#16B364]"
                                                    }`}
                                            >
                                                {row.costPercentage.toFixed(1)}%
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-5 text-left text-[14px] leading-5 font-bold text-[#0E8965] dark:text-darkGreenColor-dark whitespace-nowrap">
                                        {row.isChoiceCost ? dash : isPlaceholder || row.price === 0 ? "-" : formatCurrency(row.margin)}
                                    </td>
                                    <td className="px-4 py-3 text-left whitespace-nowrap">
                                        {row.isChoiceCost ? dash : row.popularity === 0 ? (
                                            <span className="text-[#6B7280] dark:text-textTertiary-dark">-</span>
                                        ) : (
                                            <span
                                                className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-3 h-8 text-[14px] leading-5 font-semibold border ${row.isPopularityHigh
                                                    ? "border-[#73E2A3] dark:border-[#16B364] bg-[#EDFCF2] dark:bg-[#EDFCF2]/20 text-[#099250] dark:text-[#16B364]"
                                                    : "border-[#FDA29B] dark:border-[#F04438] bg-[#FEF3F2] dark:bg-[#FEF3F2]/20 text-[#D92D20] dark:text-[#F04438]"
                                                    }`}
                                            >
                                                {row.isPopularityHigh ? (
                                                    <img src="/assets/trending-up.svg" alt="trending-up" />
                                                ) : (
                                                    <img src="/assets/trending-down.svg" alt="trending-down" />
                                                )}
                                                <span className="underline underline-offset-2 -mt-0.5">{row.popularity}</span>
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 text-right text-[#9CA3AF] dark:text-textTertiary-dark">
                                        <button
                                            type="button"
                                            className="cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDishClick && onDishClick(row);
                                            }}
                                        >
                                            <img src="/assets/chevron-right.svg" alt="chevron-right" width={24} height={24} className="min-w-6" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default DishTable;
